<!-- Orbi "Make an Appointment" card (PCSA-13241). Copy mirrors mobile's
     LabSearchOptionCard.makeAppointment; the two search criteria are prefilled
     from the agent's AppointmentFindLab payload and stay editable. Both are
     required before a search runs (AC 5) — errors surface inline under their own
     field, worded and styled as the approved Figma draws them.

     Field ORDER follows the Figma: instructions, the availability note, then
     "Reason for your visit" ABOVE "Enter ZIP or Address" — the service narrows
     which labs can even be returned, so it is chosen first. -->
<section class="orbi-find-lab-card" aria-label="Make an appointment">
  <form class="orbi-find-lab-card__card" [formGroup]="searchForm" (ngSubmit)="onSubmit()" novalidate>
    <!-- `calendar_month` is the glyph the Figma frame exports (Material /
         Upcoming) — a calendar with a 3x2 dot grid. This card used to draw
         `edit_calendar` (a calendar with a pencil), which is a different icon.
         The ROUND variant is the match as well as the app-wide idiom: outlined
         and sharp square off the dots. -->
    <div class="orbi-find-lab-card__head">
      <span class="material-icons-round orbi-find-lab-card__icon" aria-hidden="true">calendar_month</span>
      <h3 class="orbi-find-lab-card__title">{{ title }}</h3>
    </div>

    <p class="orbi-find-lab-card__instructions">{{ instructions }}</p>

    <!-- Service-availability note — directly under the instructions per Figma. -->
    <p class="orbi-find-lab-card__note">{{ serviceNote }}</p>

    <!-- Reason for visit: the portal service catalog, preselected when the agent
         sent a matching initialService (by id or name). The label is passed INTO
         <ui-select> rather than hand-rolled above it — that is what every other
         call site in the portal does, and it keeps label/control association the
         shared component's concern instead of adding a `for` here that points at
         an id ng-select never renders.

         `appendTo="body"` fixes the option list being CUT OFF at the bottom of
         the conversation (design review 08/11 — it looked like it was drawn
         "behind" the ZIP field). The panel is `position: absolute`, and this card
         sits inside the thread feed, which is `overflow-y: auto` — a scroll
         container clips absolutely-positioned descendants no matter how high
         their `z-index` is, so the panel's own 1050 could not lift it out.
         Rendering it into `body` escapes that clip; ng-select then tracks the
         field's live position while the panel is open. -->
    <div class="orbi-find-lab-card__field">
      <ui-select
        domId="orbi-find-lab-service"
        [control]="serviceControl"
        [items]="serviceItems"
        [searchable]="true"
        [label]="serviceLabel"
        appendTo="body"
        [invalid]="!!serviceErrorMessage"
        [placeholder]="isLoadingServices ? 'Loading services...' : servicePlaceholder"
      ></ui-select>
      <p *ngIf="serviceErrorMessage" class="orbi-find-lab-card__error" role="alert">
        <span class="material-icons-round icon-sm orbi-find-lab-card__error-icon" aria-hidden="true">error_outline</span>
        <span class="orbi-find-lab-card__error-text">{{ serviceErrorMessage }}</span>
      </p>
    </div>

    <!-- Location: ZIP or address, prefilled from initialLocation, with the
         portal's own ZIP/address suggestions attached (PCSA-13551 — it shipped as
         a bare input, so no lookup ever ran). `locationSuggestions` is the shared
         three-source lookup the Find a Lab modal uses, so the rows, their order
         and the 300ms/3-character threshold are the modal's, not a second set.

         `container="body"` for the same reason the <ui-select> above it needs
         `appendTo="body"`: the panel is `position: absolute` inside the thread
         feed's `overflow-y: auto`, and a scroll container clips absolutely
         positioned descendants regardless of `z-index` — the dropdown would be
         cut off at the bottom of the conversation.

         `autocomplete="off"` is BOTH the element's attribute and NgbTypeahead's
         own input of that name (it host-binds it): the browser's saved-address
         popover would otherwise cover the suggestion list. The field kept
         `postal-code` while it had no dropdown to compete with.

         The Figma draws a trailing magnifier INSIDE the field; it is decorative
         (the CTA below submits), so it is not a second control to tab to. -->
    <div class="orbi-find-lab-card__field">
      <label for="orbi-find-lab-location">{{ locationLabel }}</label>
      <div class="orbi-find-lab-card__input-wrap" [class.orbi-find-lab-card__input-wrap--invalid]="!!locationErrorMessage">
        <input
          id="orbi-find-lab-location"
          type="text"
          class="orbi-find-lab-card__input"
          formControlName="location"
          autocomplete="off"
          container="body"
          [ngbTypeahead]="locationSuggestions"
          [resultFormatter]="suggestionFormatter"
          (selectItem)="onSelectSuggestion($event)"
          [attr.aria-invalid]="!!locationErrorMessage"
          [attr.aria-describedby]="locationErrorMessage ? 'orbi-find-lab-location-error' : null"
        />
        <span class="material-icons-round orbi-find-lab-card__input-icon" aria-hidden="true">search</span>
      </div>
      <p *ngIf="locationErrorMessage" id="orbi-find-lab-location-error" class="orbi-find-lab-card__error" role="alert">
        <span class="material-icons-round icon-sm orbi-find-lab-card__error-icon" aria-hidden="true">error_outline</span>
        <span class="orbi-find-lab-card__error-text">{{ locationErrorMessage }}</span>
      </p>
    </div>

    <!-- Primary CTA. Content-width from XL (the wide frame) and full-width below
         it (the narrow frame), the same responsive rule the appointment card's
         action row follows.

         `size="small"` with `minWidth` left at ui-button's own 128 default, so
         this pill measures exactly what the Results card's "View Details" does —
         32px tall, 16px radius, 128px floor (design review 08/11: "the Find a lab
         location and similar button in all cards should be of same size as View
         Details"). It used to render at the 48px default with the floor forced
         off, which made it the one Orbi CTA off the shared scale. -->
    <div class="orbi-find-lab-card__actions">
      <ui-button class="orbi-find-lab-card__btn" [buttonText]="submitLabel" buttonType="submit" rank="primary" size="small"></ui-button>
    </div>
  </form>
</section>
